import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { gql } from "graphql-request";
import { graphqlClient } from "@/lib/graphql";
import { resizeImage, fileToBase64 } from "@/lib/imageResize";
import {
  rotateImage,
  rotateBoundingBox90CW,
  rotateBoundingBox90CCW,
  rotateBoundingBoxNTimes,
} from "@/lib/imageRotate";
import { cropImageToAspect, computeCropWindow, transformBoundingBoxForCrop } from "@/lib/imageCrop";
import type { CropOffset } from "@/lib/imageCrop";
import { ImageCropFrame } from "@/components/listings/ImageCropFrame";
import { uploadFiles } from "@/lib/directUpload";
import { useAuth } from "@/hooks/useAuth";
import { LocationAutocomplete } from "@/components/ui/LocationAutocomplete";
import { BoundingBoxOverlay } from "@/components/listings/BoundingBoxOverlay";
import type { Game, BggSearchResult, RecognizedGame, DeliveryPriceSuggestion, BoundingBox, ListingSibling } from "@/types";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { GamePlaceholder } from "@/components/ui/GamePlaceholder";
import { DeliveryPriceSuggestions } from "@/components/listings/DeliveryPriceSuggestions";
import { PriceDetailsPopover, getConditionAdjustedPrice } from "@/components/listings/PriceDetailsPopover";
import { GameSelector } from "@/components/listings/GameSelector";
import { Modal } from "@/components/ui/Modal";

const SEARCH_GAMES_QUERY = gql`
  query SearchGames($query: String!) {
    searchGames(query: $query) {
      id
      title
      yearPublished
      thumbnailUrl
      languageIndependent
      suggestedPrice
      suggestedPriceInfo {
        price
        newPrice
        shopName
      }
      shopListings {
        id
        shopName
        listingUrl
        price
        listingName
      }
    }
  }
`;

const SEARCH_BGG_MUTATION = gql`
  mutation SearchBggGame($query: String!) {
    searchBggGame(query: $query) {
      results {
        bggId
        title
        yearPublished
      }
    }
  }
`;

const IMPORT_BGG_MUTATION = gql`
  mutation ImportBggGame($bggId: Int!) {
    importBggGame(bggId: $bggId) {
      game {
        id
        title
        yearPublished
        thumbnailUrl
        languageIndependent
        suggestedPrice
        suggestedPriceInfo {
          price
          newPrice
          shopName
        }
        shopListings {
          id
          shopName
          listingUrl
          price
          listingName
        }
      }
      errors
    }
  }
`;

const RECOGNIZE_GAME_MUTATION = gql`
  mutation RecognizeGameImage($imageData: String!, $contentType: String) {
    recognizeGameImage(imageData: $imageData, contentType: $contentType) {
      recognizedGames {
        name
        year
        confidence
        detectedLanguage
        boundingBox {
          yMin
          xMin
          yMax
          xMax
        }
        gameMatch {
          id
          title
          yearPublished
          thumbnailUrl
          languageIndependent
          suggestedPrice
          suggestedPriceInfo {
            price
            newPrice
            shopName
          }
          shopListings {
            id
            shopName
            listingUrl
            price
            listingName
          }
        }
      }
      errors
    }
  }
`;

const CREATE_LISTING_MUTATION = gql`
  mutation CreateListing(
    $gameId: ID
    $title: String
    $price: Float!
    $condition: String!
    $conditionNotes: String
    $language: String
    $shippingAvailable: Boolean
    $shippingCost: Float
    $location: String
    $deliveryAvailable: Boolean
    $pickupAvailable: Boolean
    $pickupCity: String
    $pickupLatitude: Float
    $pickupLongitude: Float
    $pickupRadius: Int
    $imageSignedIds: [String!]
    $gameUrl: String
    $saveAsDefaultLocation: Boolean
  ) {
    createListing(
      gameId: $gameId
      title: $title
      price: $price
      condition: $condition
      conditionNotes: $conditionNotes
      language: $language
      shippingAvailable: $shippingAvailable
      shippingCost: $shippingCost
      location: $location
      deliveryAvailable: $deliveryAvailable
      pickupAvailable: $pickupAvailable
      pickupCity: $pickupCity
      pickupLatitude: $pickupLatitude
      pickupLongitude: $pickupLongitude
      pickupRadius: $pickupRadius
      imageSignedIds: $imageSignedIds
      gameUrl: $gameUrl
      saveAsDefaultLocation: $saveAsDefaultLocation
    ) {
      listing {
        id
      }
      errors
    }
  }
`;

const CREATE_LISTINGS_MUTATION = gql`
  mutation CreateListings($listings: [ListingInput!]!, $imageSignedIds: [String!], $saveAsDefaultLocation: Boolean) {
    createListings(listings: $listings, imageSignedIds: $imageSignedIds, saveAsDefaultLocation: $saveAsDefaultLocation) {
      listings {
        id
        listingGroupId
      }
      errors
    }
  }
`;

const GAME_DELIVERY_SUGGESTIONS_QUERY = gql`
  query GameDeliverySuggestions($id: ID!) {
    game(id: $id) {
      deliveryPriceSuggestions {
        carrier
        tier
        rawPrice
        suggestedPrice
        recommended
      }
    }
  }
`;

const CONDITIONS = [
  { value: "new", label: "Nauja" },
  { value: "like_new", label: "Labai gera" },
  { value: "good", label: "Gera" },
  { value: "acceptable", label: "Patenkinama" },
];

const PRIORITY_LANGUAGES = ["universal", "en", "lt", "ru"];
const OTHER_LANGUAGES = ["de", "fr", "pl", "es", "it", "nl", "pt", "zh", "ja", "ko"];
const ALL_LANGUAGES = [...PRIORITY_LANGUAGES, ...OTHER_LANGUAGES];

function normalizeDetectedLanguage(code: string | null | undefined): string | null {
  if (!code) return null;
  const lower = code.toLowerCase();
  return ALL_LANGUAGES.includes(lower) ? lower : null;
}
const LANGUAGE_LABELS: Record<string, string> = {
  universal: "Universali (nuo kalbos nepriklausomas)",
  en: "Anglų",
  lt: "Lietuvių",
  ru: "Rusų",
  de: "Vokiečių",
  fr: "Prancūzų",
  pl: "Lenkų",
  es: "Ispanų",
  it: "Italų",
  nl: "Olandų",
  pt: "Portugalų",
  zh: "Kinų",
  ja: "Japonų",
  ko: "Korėjiečių",
};

interface ImagePreview {
  file: File;
  url: string;
}

interface SuggestedPriceInfo {
  price: number;
  newPrice: number;
  shopName: string;
}

interface ShopListingInfo {
  id: string;
  shopName: string;
  listingUrl: string;
  price: number | null;
  listingName: string | null;
}

type GameInfo = Pick<Game, "id" | "title" | "yearPublished" | "thumbnailUrl" | "languageIndependent"> & {
  suggestedPrice?: number | null;
  suggestedPriceInfo?: SuggestedPriceInfo | null;
  shopListings?: ShopListingInfo[];
};

interface MultiGameEntry {
  recognized: RecognizedGame;
  selected: boolean;
  gameInfo: GameInfo | null;
  price: string;
  condition: string;
  conditionNotes: string;
  language: string;
  gameUrl: string;
  customTitle: string;
}

export function NewListingPage() {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();

  // Images
  const [images, setImages] = useState<ImagePreview[]>([]);
  const [isRecognizing, setIsRecognizing] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Crop + confirmation gate
  const CARD_ASPECT_RATIO = 3 / 2;
  const [cropOffset, setCropOffset] = useState<CropOffset>({ x: 0.5, y: 0.5 });
  const [imageConfirmed, setImageConfirmed] = useState(false);
  const [isFinalizing, setIsFinalizing] = useState(false);
  // Raw AI recognition result (bboxes in pre-crop, post-rotation 0..1000 space).
  // Populated as soon as AI returns; promoted to recognitionResults/multiGameEntries
  // only after the user confirms the crop.
  const rawRecognitionRef = useRef<RecognizedGame[] | null>(null);
  const [recognitionReady, setRecognitionReady] = useState(false);
  const [recognitionFailed, setRecognitionFailed] = useState(false);

  // AI recognition results
  const [recognitionResults, setRecognitionResults] = useState<RecognizedGame[]>([]);

  // Multi-game mode
  const [multiGameMode, setMultiGameMode] = useState(false);
  const [multiGameEntries, setMultiGameEntries] = useState<MultiGameEntry[]>([]);
  const [editingSingleTitle, setEditingSingleTitle] = useState(false);
  const [editingGameIndex, setEditingGameIndex] = useState<number | null>(null);
  const [conditionChangedIndexes, setConditionChangedIndexes] = useState<Set<number>>(new Set());

  // Game selection (single mode)
  const [selectedGame, setSelectedGame] = useState<GameInfo | null>(null);
  const [gameSearch, setGameSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [debouncedBggSearch, setDebouncedBggSearch] = useState("");
  const [importingBggId, setImportingBggId] = useState<number | null>(null);

  const [customTitle, setCustomTitle] = useState("");
  const [gameUrl, setGameUrl] = useState("");

  // Form fields
  const [price, setPrice] = useState("");
  const [condition, setCondition] = useState("like_new");
  const [conditionNotes, setConditionNotes] = useState("");
  const [language, setLanguage] = useState("en");
  const [shippingAvailable, setShippingAvailable] = useState(true);
  const [shippingCost, setShippingCost] = useState("");
  const [location, setLocation] = useState("");
  const [errors, setErrors] = useState<string[]>([]);

  // Delivery & Pickup
  const [deliveryAvailable, setDeliveryAvailable] = useState(true);
  const [pickupAvailable, setPickupAvailable] = useState(true);
  const [pickupCity, setPickupCity] = useState("");
  const [pickupLatitude, setPickupLatitude] = useState<number | null>(null);
  const [pickupLongitude, setPickupLongitude] = useState<number | null>(null);
  const [pickupRadius, setPickupRadius] = useState("0");

  // Location save-as-default & compact display
  const [saveAsDefaultLocation, setSaveAsDefaultLocation] = useState(false);
  const [showAddressEditor, setShowAddressEditor] = useState(false);
  const userHasLocation = !!(user?.latitude != null && user?.longitude != null);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Pre-fill from user defaults
  useEffect(() => {
    if (user) {
      setDeliveryAvailable(user.defaultDelivery ?? true);
      setPickupAvailable(user.defaultPickup ?? true);
      setPickupRadius(String(user.defaultPickupRadius ?? 0));
      if (user.city) {
        setPickupCity(user.city);
        setLocation(user.city);
      }
      if (user.latitude != null) setPickupLatitude(user.latitude);
      if (user.longitude != null) setPickupLongitude(user.longitude);
      // Also pre-fill shipping from delivery default
      setShippingAvailable(user.defaultDelivery ?? true);
      // If user has no location set, default to checked for first listing
      if (user.latitude == null || user.longitude == null) {
        setSaveAsDefaultLocation(true);
        setShowAddressEditor(true);
      }
    }
  }, [user]);

  // Auto-set language to "universal" when a language-independent game is selected
  useEffect(() => {
    if (selectedGame?.languageIndependent) {
      setLanguage("universal");
    }
  }, [selectedGame]);

  // Cleanup preview URLs on unmount
  useEffect(() => {
    return () => {
      images.forEach((img) => URL.revokeObjectURL(img.url));
    };
  }, []);

  // Debounce BGG search on search change
  useEffect(() => {
    if (gameSearch.length < 2) {
      setDebouncedBggSearch("");
      return;
    }
    const timer = setTimeout(() => setDebouncedBggSearch(gameSearch), 500);
    return () => clearTimeout(timer);
  }, [gameSearch]);

  const { data: searchResults, isFetching: isSearching } = useQuery({
    queryKey: ["searchGames", gameSearch],
    queryFn: () =>
      graphqlClient.request<{ searchGames: GameInfo[] }>(
        SEARCH_GAMES_QUERY,
        { query: gameSearch }
      ),
    enabled: gameSearch.length >= 2,
  });

  const { data: bggResults, isFetching: isBggSearching } = useQuery({
    queryKey: ["searchBggGames", debouncedBggSearch],
    queryFn: () =>
      graphqlClient.request<{ searchBggGame: { results: BggSearchResult[] } }>(
        SEARCH_BGG_MUTATION,
        { query: debouncedBggSearch }
      ),
    enabled: debouncedBggSearch.length >= 2,
  });

  const suggestedPriceInfo = selectedGame?.suggestedPriceInfo ?? null;

  const { data: deliverySuggestionsData } = useQuery({
    queryKey: ["gameDeliverySuggestions", selectedGame?.id],
    queryFn: () =>
      graphqlClient.request<{
        game: { deliveryPriceSuggestions: DeliveryPriceSuggestion[] };
      }>(GAME_DELIVERY_SUGGESTIONS_QUERY, { id: selectedGame!.id }),
    enabled: !!selectedGame?.id && deliveryAvailable,
  });

  const deliverySuggestions =
    deliverySuggestionsData?.game?.deliveryPriceSuggestions ?? [];

  const importBggMutation = useMutation({
    mutationFn: (bggId: number) =>
      graphqlClient.request<{ importBggGame: { game: GameInfo | null; errors: string[] } }>(
        IMPORT_BGG_MUTATION,
        { bggId }
      ),
  });

  const recognizeMutation = useMutation({
    mutationFn: (vars: { imageData: string; contentType: string }) =>
      graphqlClient.request<{
        recognizeGameImage: { recognizedGames: RecognizedGame[]; errors: string[] };
      }>(RECOGNIZE_GAME_MUTATION, vars),
  });

  const createMutation = useMutation({
    mutationFn: (vars: {
      gameId?: string;
      title?: string;
      price: number;
      condition: string;
      conditionNotes?: string;
      language?: string;
      shippingAvailable: boolean;
      shippingCost?: number;
      location?: string;
      deliveryAvailable: boolean;
      pickupAvailable: boolean;
      pickupCity?: string;
      pickupLatitude?: number;
      pickupLongitude?: number;
      pickupRadius?: number;
      imageSignedIds?: string[];
      gameUrl?: string;
      saveAsDefaultLocation?: boolean;
    }) =>
      graphqlClient.request<{
        createListing: { listing: { id: string } | null; errors: string[] };
      }>(CREATE_LISTING_MUTATION, vars),
  });

  const createListingsMutation = useMutation({
    mutationFn: (vars: {
      listings: Array<{
        gameId?: string;
        price: number;
        condition: string;
        conditionNotes?: string;
        language?: string;
        shippingAvailable?: boolean;
        shippingCost?: number;
        location?: string;
        deliveryAvailable?: boolean;
        pickupAvailable?: boolean;
        pickupCity?: string;
        pickupLatitude?: number;
        pickupLongitude?: number;
        pickupRadius?: number;
        bboxYMin?: number;
        bboxXMin?: number;
        bboxYMax?: number;
        bboxXMax?: number;
      }>;
      imageSignedIds?: string[];
      saveAsDefaultLocation?: boolean;
    }) =>
      graphqlClient.request<{
        createListings: { listings: { id: string; listingGroupId: string | null }[] | null; errors: string[] };
      }>(CREATE_LISTINGS_MUTATION, vars),
  });

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setShowDropdown(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const userHasTypedRef = useRef(false);

  // Counts 90° CW rotations applied to images[0] while the AI recognition
  // request is in-flight. The AI sees the un-rotated original, so when the
  // response arrives we transform its bounding boxes by this many steps to
  // align them with whatever rotated image the user is now seeing.
  const pendingRotationsRef = useRef(0);

  const updateMultiGameEntry = (index: number, updates: Partial<MultiGameEntry>) => {
    setMultiGameEntries((prev) =>
      prev.map((entry, i) => (i === index ? { ...entry, ...updates } : entry))
    );
  };

  const applyConditionToBelow = (index: number, condition: string) => {
    setMultiGameEntries((prev) =>
      prev.map((entry, i) => (i > index ? { ...entry, condition } : entry))
    );
    setConditionChangedIndexes((prev) => {
      const next = new Set(prev);
      next.delete(index);
      return next;
    });
  };

  const handleSelectRecognitionResult = (game: RecognizedGame) => {
    if (game.gameMatch) {
      setSelectedGame(game.gameMatch);
    } else {
      setGameSearch(game.name);
      setShowDropdown(true);
    }
  };

  const handleAddImages = useCallback(
    async (files: FileList | File[]) => {
      const fileArr = Array.from(files);
      const resized = await Promise.all(fileArr.map(resizeImage));
      const newPreviews: ImagePreview[] = resized.map((file) => ({
        file,
        url: URL.createObjectURL(file),
      }));

      const updated = [...images, ...newPreviews];
      setImages(updated);

      // Trigger AI recognition on first image (runs silently in background while
      // the user adjusts crop / rotation).
      if (images.length === 0 && fileArr.length > 0) {
        setIsRecognizing(true);
        setRecognitionResults([]);
        setMultiGameMode(false);
        setMultiGameEntries([]);
        setRecognitionReady(false);
        setRecognitionFailed(false);
        rawRecognitionRef.current = null;
        pendingRotationsRef.current = 0;
        try {
          const base64 = await fileToBase64(resized[0]);
          const result = await recognizeMutation.mutateAsync({
            imageData: base64,
            contentType: "image/jpeg",
          });
          // Snapshot any rotations the user applied while waiting on AI, then
          // reset so subsequent rotations flow through handleRotateImage and
          // transform the now-set bounding boxes directly.
          const rotationsDuringRecognition = pendingRotationsRef.current;
          pendingRotationsRef.current = 0;
          const recognized = result.recognizeGameImage.recognizedGames.map((game) => ({
            ...game,
            boundingBox: game.boundingBox
              ? rotateBoundingBoxNTimes(game.boundingBox, rotationsDuringRecognition)
              : game.boundingBox,
          }));
          rawRecognitionRef.current = recognized;
          setRecognitionReady(true);
        } catch {
          rawRecognitionRef.current = [];
          setRecognitionReady(true);
          setRecognitionFailed(true);
        } finally {
          setIsRecognizing(false);
        }
      }
    },
    [images, recognizeMutation]
  );

  function applyRecognition(recognized: RecognizedGame[]) {
    if (recognized.length > 1) {
      setRecognitionResults(recognized);
      setMultiGameMode(true);
      setMultiGameEntries(
        recognized.map((game) => {
          const detected = normalizeDetectedLanguage(game.detectedLanguage);
          const defaultLang = game.gameMatch?.languageIndependent
            ? "universal"
            : detected || "en";
          return {
            recognized: game,
            selected: true,
            gameInfo: game.gameMatch || null,
            price: game.gameMatch?.suggestedPriceInfo
              ? String(getConditionAdjustedPrice(game.gameMatch.suggestedPriceInfo.newPrice, "like_new"))
              : "",
            condition: "like_new",
            conditionNotes: "",
            language: defaultLang,
            gameUrl: "",
            customTitle: "",
          };
        })
      );
    } else if (recognized.length === 1) {
      if (recognized[0].gameMatch) {
        setSelectedGame(recognized[0].gameMatch);
      } else if (!userHasTypedRef.current) {
        setGameSearch(recognized[0].name);
        setShowDropdown(true);
      }
      const detected = normalizeDetectedLanguage(recognized[0].detectedLanguage);
      if (detected && !recognized[0].gameMatch?.languageIndependent) {
        setLanguage(detected);
      }
    }
  }

  function waitForRecognition(): Promise<void> {
    if (recognitionReady || rawRecognitionRef.current !== null) return Promise.resolve();
    return new Promise((resolve) => {
      const check = () => {
        if (rawRecognitionRef.current !== null) resolve();
        else setTimeout(check, 100);
      };
      check();
    });
  }

  async function handleConfirmCrop() {
    if (images.length === 0 || isFinalizing) return;
    setIsFinalizing(true);
    try {
      const firstImage = images[0];
      const naturalSize = await loadImageNaturalSize(firstImage.url);
      const cropWindow = computeCropWindow(
        naturalSize.w,
        naturalSize.h,
        CARD_ASPECT_RATIO,
        cropOffset
      );
      const croppedFile = await cropImageToAspect(firstImage.file, CARD_ASPECT_RATIO, cropOffset);
      const croppedUrl = URL.createObjectURL(croppedFile);
      URL.revokeObjectURL(firstImage.url);
      setImages((prev) => [
        { file: croppedFile, url: croppedUrl },
        ...prev.slice(1),
      ]);

      await waitForRecognition();

      const raw = rawRecognitionRef.current || [];
      const transformed = raw
        .map((game) => {
          if (!game.boundingBox) return game;
          const newBbox = transformBoundingBoxForCrop(game.boundingBox, cropWindow);
          return { ...game, boundingBox: newBbox };
        })
        .filter((game) => !game.boundingBox || game.boundingBox); // keep all for now
      applyRecognition(transformed);
      setImageConfirmed(true);
    } finally {
      setIsFinalizing(false);
    }
  }

  function loadImageNaturalSize(url: string): Promise<{ w: number; h: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve({ w: img.naturalWidth, h: img.naturalHeight });
      img.onerror = () => reject(new Error("Could not load image"));
      img.src = url;
    });
  }

  const handleRemoveImage = (index: number) => {
    setImages((prev) => {
      URL.revokeObjectURL(prev[index].url);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleRotateImage = async (index: number, direction: "cw" | "ccw" = "cw") => {
    const original = images[index];
    const rotatedFile = await rotateImage(original.file, direction);
    URL.revokeObjectURL(original.url);
    const newUrl = URL.createObjectURL(rotatedFile);
    setImages((prev) =>
      prev.map((img, i) =>
        i === index ? { file: rotatedFile, url: newUrl } : img
      )
    );

    // Only the first image drives AI recognition and bounding boxes. For any
    // other image, the rotation is purely cosmetic.
    if (index !== 0) return;

    // Reset crop offset to center since the axis that was overflowing may have
    // swapped after rotation.
    setCropOffset({ x: 0.5, y: 0.5 });

    const rotateBbox = direction === "cw" ? rotateBoundingBox90CW : rotateBoundingBox90CCW;

    // If the recognition request is still in flight, accumulate rotations so
    // the handler that receives the response can apply them to the returned
    // boxes. Otherwise the boxes are already in rawRecognitionRef (or in
    // post-confirm state) and we transform them here.
    if (isRecognizing) {
      // pendingRotationsRef tracks net CW rotations; CCW subtracts.
      pendingRotationsRef.current += direction === "cw" ? 1 : -1;
    } else if (rawRecognitionRef.current) {
      rawRecognitionRef.current = rawRecognitionRef.current.map((game) => ({
        ...game,
        boundingBox: game.boundingBox ? rotateBbox(game.boundingBox) : game.boundingBox,
      }));
    }
    setMultiGameEntries((prev) =>
      prev.map((entry) => ({
        ...entry,
        recognized: {
          ...entry.recognized,
          boundingBox: entry.recognized.boundingBox
            ? rotateBbox(entry.recognized.boundingBox)
            : entry.recognized.boundingBox,
        },
      }))
    );
    setRecognitionResults((prev) =>
      prev.map((game) => ({
        ...game,
        boundingBox: game.boundingBox
          ? rotateBbox(game.boundingBox)
          : game.boundingBox,
      }))
    );
  };

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (files.length > 0) handleAddImages(files);
    },
    [handleAddImages]
  );

  const handleImportBgg = async (bggId: number) => {
    setImportingBggId(bggId);
    try {
      const data = await importBggMutation.mutateAsync(bggId);
      if (data.importBggGame.game) {
        setSelectedGame(data.importBggGame.game);
        setCustomTitle("");
        setGameUrl("");
        setGameSearch("");
        setShowDropdown(false);
      }
    } finally {
      setImportingBggId(null);
    }
  };

  const handleSubmitSingle = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);

    // Use search text as custom title if no game selected and no explicit custom title
    const effectiveTitle = customTitle || gameSearch.trim();
    if (!selectedGame && !effectiveTitle) {
      setErrors(["Pasirinkite žaidimą"]);
      return;
    }
    if (!selectedGame && !customTitle && effectiveTitle) {
      setCustomTitle(effectiveTitle);
      setShowDropdown(false);
    }

    const priceNum = parseFloat(price);
    if (isNaN(priceNum) || priceNum <= 0) {
      setErrors(["Įveskite teisingą kainą"]);
      return;
    }

    if (!deliveryAvailable && !pickupAvailable) {
      setErrors(["Pasirinkite bent vieną pristatymo būdą"]);
      return;
    }

    setIsUploading(true);

    try {
      let imageSignedIds: string[] | undefined;
      if (images.length > 0) {
        imageSignedIds = await uploadFiles(images.map((img) => img.file));
      }

      const result = await createMutation.mutateAsync({
        gameId: selectedGame?.id || undefined,
        title: effectiveTitle || undefined,
        price: priceNum,
        condition,
        conditionNotes: conditionNotes || undefined,
        language: language || undefined,
        shippingAvailable,
        shippingCost: shippingAvailable && shippingCost ? parseFloat(shippingCost) : undefined,
        location: location || undefined,
        deliveryAvailable,
        pickupAvailable,
        pickupCity: pickupAvailable ? pickupCity || undefined : undefined,
        pickupLatitude: pickupAvailable && pickupLatitude != null ? pickupLatitude : undefined,
        pickupLongitude: pickupAvailable && pickupLongitude != null ? pickupLongitude : undefined,
        pickupRadius: pickupAvailable ? parseInt(pickupRadius, 10) : undefined,
        imageSignedIds,
        gameUrl: gameUrl || undefined,
        saveAsDefaultLocation: saveAsDefaultLocation || undefined,
      });

      if (result.createListing.listing) {
        // Update local user state if location was saved as default
        if (saveAsDefaultLocation && user && pickupLatitude != null && pickupLongitude != null) {
          updateUser({
            ...user,
            latitude: pickupLatitude,
            longitude: pickupLongitude,
            city: pickupCity || user.city,
            address: location || user.address,
            defaultPickupRadius: parseInt(pickupRadius, 10),
          });
        }
        navigate(`/listings/${result.createListing.listing.id}`);
      } else {
        setErrors(result.createListing.errors);
        setIsUploading(false);
      }
    } catch {
      setErrors(["Nepavyko įkelti nuotraukų"]);
      setIsUploading(false);
    }
  };

  const handleSubmitMulti = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors([]);

    const selectedEntries = multiGameEntries.filter((e) => e.selected);

    if (selectedEntries.length === 0) {
      setErrors(["Pasirinkite bent vieną žaidimą"]);
      return;
    }

    const invalidPrices = selectedEntries.filter((e) => {
      const p = parseFloat(e.price);
      return isNaN(p) || p <= 0;
    });

    if (invalidPrices.length > 0) {
      setErrors(["Įveskite teisingas kainas visiems pasirinktiems žaidimams"]);
      return;
    }

    if (!deliveryAvailable && !pickupAvailable) {
      setErrors(["Pasirinkite bent vieną pristatymo būdą"]);
      return;
    }

    setIsUploading(true);

    try {
      let imageSignedIds: string[] | undefined;
      if (images.length > 0) {
        imageSignedIds = await uploadFiles(images.map((img) => img.file));
      }

      const listings = selectedEntries.map((entry) => ({
        gameId: entry.gameInfo?.id || undefined,
        title: entry.customTitle || (entry.gameInfo ? undefined : entry.recognized.name),
        yearPublished: entry.gameInfo ? undefined : (entry.recognized.year ?? undefined),
        price: parseFloat(entry.price),
        condition: entry.condition,
        conditionNotes: entry.conditionNotes || undefined,
        language: entry.language || undefined,
        gameUrl: entry.gameUrl || undefined,
        shippingAvailable,
        shippingCost: shippingAvailable && shippingCost ? parseFloat(shippingCost) : undefined,
        location: location || undefined,
        deliveryAvailable,
        pickupAvailable,
        pickupCity: pickupAvailable ? pickupCity || undefined : undefined,
        pickupLatitude: pickupAvailable && pickupLatitude != null ? pickupLatitude : undefined,
        pickupLongitude: pickupAvailable && pickupLongitude != null ? pickupLongitude : undefined,
        pickupRadius: pickupAvailable ? parseInt(pickupRadius, 10) : undefined,
        ...(entry.recognized.boundingBox
          ? {
              bboxYMin: entry.recognized.boundingBox.yMin,
              bboxXMin: entry.recognized.boundingBox.xMin,
              bboxYMax: entry.recognized.boundingBox.yMax,
              bboxXMax: entry.recognized.boundingBox.xMax,
            }
          : {}),
      }));

      const result = await createListingsMutation.mutateAsync({
        listings,
        imageSignedIds,
        saveAsDefaultLocation: saveAsDefaultLocation || undefined,
      });

      if (result.createListings.listings && result.createListings.listings.length > 0) {
        // Update local user state if location was saved as default
        if (saveAsDefaultLocation && user && pickupLatitude != null && pickupLongitude != null) {
          updateUser({
            ...user,
            latitude: pickupLatitude,
            longitude: pickupLongitude,
            city: pickupCity || user.city,
            address: location || user.address,
            defaultPickupRadius: parseInt(pickupRadius, 10),
          });
        }
        const groupId = result.createListings.listings[0].listingGroupId;
        if (groupId) {
          navigate(`/listing-groups/${groupId}`);
        } else {
          navigate(`/listings/${result.createListings.listings[0].id}`);
        }
      } else {
        setErrors(result.createListings.errors);
        setIsUploading(false);
      }
    } catch {
      setErrors(["Nepavyko sukurti skelbimų"]);
      setIsUploading(false);
    }
  };

  const isSubmitting = isUploading || createMutation.isPending || createListingsMutation.isPending;

  // Check if we have bounding box data for overlay
  const gamesWithBoundingBoxes = multiGameMode
    ? multiGameEntries
        .filter((e) => e.selected && e.recognized.boundingBox)
        .map((e) => ({
          name: e.recognized.name,
          boundingBox: e.recognized.boundingBox!,
        }))
    : [];

  const selectedCount = multiGameEntries.filter((e) => e.selected).length;

  return (
    <div className="mx-auto max-w-2xl px-4 py-8 sm:px-6 lg:px-8">
      <h1 className="mb-8 text-2xl font-bold text-gray-900">Naujas skelbimas</h1>

      {errors.length > 0 && (
        <div className="mb-6 rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {errors.map((err, i) => (
            <p key={i}>{err}</p>
          ))}
        </div>
      )}

      {/* Photo upload card */}
      <div className="mb-6 rounded-lg border border-gray-200 bg-gray-50 p-4">
        {images.length === 0 ? (
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
            style={{ aspectRatio: String(CARD_ASPECT_RATIO) }}
            className="flex w-full cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed border-gray-300 transition-colors hover:border-primary-800 hover:bg-gray-100"
          >
            <svg
              className="mb-3 h-10 w-10 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4.5v15m7.5-7.5h-15"
              />
            </svg>
            <p className="text-sm font-medium text-gray-600">Įkelti nuotraukų</p>
            <p className="mt-1 text-xs text-gray-400">arba vilkite čia</p>
            <p className="mt-3 max-w-xs text-center text-xs text-gray-400">
              Galite įkelti vieną nuotrauką su keliais žaidimais &mdash; atpažinsime kiekvieną automatiškai
            </p>
          </div>
        ) : !imageConfirmed ? (
          <div className="space-y-3">
            <div className="relative">
              <ImageCropFrame
                imageUrl={images[0].url}
                aspectRatio={CARD_ASPECT_RATIO}
                offset={cropOffset}
                onOffsetChange={setCropOffset}
              />
              <div className="absolute right-2 top-2 flex gap-1">
                <button
                  type="button"
                  onClick={() => handleRotateImage(0, "ccw")}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                  title="Pasukti prieš laikrodžio rodyklę"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 15l-6 6m0 0l-6-6m6 6V9a6 6 0 0 1 12 0v3" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => handleRotateImage(0, "cw")}
                  className="flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                  title="Pasukti pagal laikrodžio rodyklę"
                >
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 15l6 6m0 0l6-6m-6 6V9a6 6 0 0 0-12 0v3" />
                  </svg>
                </button>
              </div>
              <button
                type="button"
                onClick={() => {
                  setImages([]);
                  rawRecognitionRef.current = null;
                  setRecognitionReady(false);
                  setRecognitionFailed(false);
                  setCropOffset({ x: 0.5, y: 0.5 });
                }}
                className="absolute left-2 top-2 flex h-8 w-8 items-center justify-center rounded-full bg-black/60 text-white hover:bg-black/80"
                title="Pašalinti"
              >
                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {images.length > 1 && (
              <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
                {images.slice(1).map((img, i) => (
                  <div key={i + 1} className="group relative aspect-square">
                    <img
                      src={img.url}
                      alt={`Nuotrauka ${i + 2}`}
                      className="h-full w-full rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(i + 1)}
                      className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white"
                    >
                      &times;
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="text-sm text-primary-700 hover:text-primary-800"
              >
                + Pridėti dar nuotraukų
              </button>
              <button
                type="button"
                onClick={handleConfirmCrop}
                disabled={isFinalizing}
                className="btn-primary ml-auto flex items-center gap-2"
              >
                {isFinalizing && <LoadingSpinner className="h-4 w-4" />}
                {isFinalizing ? "Atpažįstama..." : "Tęsti"}
              </button>
            </div>
          </div>
        ) : (
          <div>
            {/* Show bounding box overlay for multi-game */}
            {multiGameMode && gamesWithBoundingBoxes.length > 0 && images[0] ? (
              <div className="mb-3">
                <BoundingBoxOverlay
                  mode="highlight"
                  games={gamesWithBoundingBoxes}
                  imageUrl={images[0].url}
                />
              </div>
            ) : (
              <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
                {images.map((img, i) => (
                  <div key={i} className="group relative aspect-square">
                    <img
                      src={img.url}
                      alt={`Nuotrauka ${i + 1}`}
                      className="h-full w-full rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(i)}
                      className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      &times;
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRotateImage(i)}
                      className="absolute -bottom-2 -right-2 flex h-6 w-6 items-center justify-center rounded-full bg-gray-600 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 15l6 6m0 0l6-6m-6 6V9a6 6 0 0 0-12 0v3" />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex aspect-square items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-gray-400 transition-colors hover:border-primary-800 hover:text-primary-800"
                >
                  <svg
                    className="h-8 w-8"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4.5v15m7.5-7.5h-15"
                    />
                  </svg>
                </button>
              </div>
            )}

            {/* Additional images in multi-game mode */}
            {multiGameMode && images.length > 1 && (
              <div className="mt-3 grid grid-cols-4 gap-2 sm:grid-cols-6">
                {images.slice(1).map((img, i) => (
                  <div key={i + 1} className="group relative aspect-square">
                    <img
                      src={img.url}
                      alt={`Nuotrauka ${i + 2}`}
                      className="h-full w-full rounded-lg object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleRemoveImage(i + 1)}
                      className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs text-white opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      &times;
                    </button>
                    <button
                      type="button"
                      onClick={() => handleRotateImage(i + 1)}
                      className="absolute -bottom-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full bg-gray-600 text-white opacity-0 transition-opacity group-hover:opacity-100"
                    >
                      <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 15l6 6m0 0l6-6m-6 6V9a6 6 0 0 0-12 0v3" />
                      </svg>
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex aspect-square items-center justify-center rounded-lg border-2 border-dashed border-gray-300 text-gray-400 transition-colors hover:border-primary-800 hover:text-primary-800"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 4.5v15m7.5-7.5h-15"
                    />
                  </svg>
                </button>
              </div>
            )}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files?.length) handleAddImages(e.target.files);
            e.target.value = "";
          }}
        />

        {imageConfirmed && recognitionFailed && (
          <div className="mt-4 rounded-lg bg-amber-50 p-3 text-sm text-amber-800">
            Nepavyko automatiškai atpažinti žaidimo. Užpildykite laukus rankiniu būdu.
          </div>
        )}

        {/* Multi-game recognition: checkboxes */}
        {imageConfirmed && multiGameMode && multiGameEntries.length > 0 && (
          <div className="mt-4">
            <p className="mb-2 text-sm font-medium text-gray-700">
              Atpažinti žaidimai ({selectedCount} iš {multiGameEntries.length} pasirinkta)
            </p>
            <div className="space-y-2">
              {multiGameEntries.map((entry, i) => (
                <label
                  key={i}
                  className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2 transition-colors ${
                    entry.selected
                      ? "border-primary-800 bg-primary-50"
                      : "border-gray-200 bg-white"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={entry.selected}
                    onChange={(e) =>
                      updateMultiGameEntry(i, { selected: e.target.checked })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-primary-800 focus:ring-primary-800"
                  />
                  <div className="flex-1">
                    <span className="text-sm font-medium text-gray-900">
                      {entry.recognized.name}
                    </span>
                    {entry.recognized.year && (
                      <span className="ml-1 text-sm text-gray-500">
                        ({entry.recognized.year})
                      </span>
                    )}
                  </div>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Single-game recognition results as clickable options */}
        {imageConfirmed && !multiGameMode && recognitionResults.length > 0 && (
          <div className="mt-4">
            <div className="flex flex-wrap gap-2">
              {recognitionResults.map((game, i) => {
                const isSelected = selectedGame && game.gameMatch &&
                  selectedGame.id === game.gameMatch.id;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleSelectRecognitionResult(game)}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-colors ${
                      isSelected
                        ? "border-primary-800 bg-primary-50 text-primary-800"
                        : "border-gray-200 bg-white text-gray-900 hover:border-primary-800 hover:bg-gray-50"
                    }`}
                  >
                    <span className="font-medium">{game.name}</span>
                    {game.year && (
                      <span className="text-gray-500">({game.year})</span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {imageConfirmed && (
      <form onSubmit={multiGameMode ? handleSubmitMulti : handleSubmitSingle} className="space-y-6">
        <div className="space-y-6 rounded-lg border border-gray-200 bg-white p-6">
          {/* Game selector (single mode only) */}
          {!multiGameMode && (
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Pavadinimas
              </label>
              {selectedGame ? (
                <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                  {selectedGame.thumbnailUrl ? (
                    <img
                      src={selectedGame.thumbnailUrl}
                      alt={selectedGame.title}
                      className="h-12 w-12 rounded object-cover"
                    />
                  ) : (
                    <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded">
                      <GamePlaceholder />
                    </div>
                  )}
                  <div className="flex-1">
                    {editingSingleTitle ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          autoFocus
                          value={customTitle}
                          onChange={(e) => setCustomTitle(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === "Escape") {
                              e.preventDefault();
                              setEditingSingleTitle(false);
                            }
                          }}
                          placeholder={selectedGame.title}
                          className="input flex-1 text-sm"
                        />
                        <button
                          type="button"
                          onClick={() => setEditingSingleTitle(false)}
                          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-green-600 text-white hover:bg-green-700"
                          aria-label="Patvirtinti"
                          title="Patvirtinti"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <p className="font-medium text-gray-900">
                          {customTitle || selectedGame.title}
                        </p>
                        {!customTitle && selectedGame.yearPublished && (
                          <span className="text-sm text-gray-500">
                            ({selectedGame.yearPublished})
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            if (!customTitle) setCustomTitle(selectedGame.title);
                            setEditingSingleTitle(true);
                          }}
                          className="text-gray-400 hover:text-gray-600"
                          aria-label="Redaguoti pavadinimą"
                          title="Redaguoti pavadinimą"
                        >
                          <svg
                            className="h-4 w-4"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z"
                            />
                          </svg>
                        </button>
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedGame(null)}
                    className="text-sm font-medium text-red-600 hover:text-red-700"
                  >
                    Pašalinti
                  </button>
                </div>
              ) : customTitle ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{customTitle}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setCustomTitle(""); setGameUrl(""); setGameSearch(""); }}
                      className="text-sm font-medium text-red-600 hover:text-red-700"
                    >
                      Pašalinti
                    </button>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      Nuoroda į žaidimą (nebūtina)
                    </label>
                    <input
                      type="url"
                      value={gameUrl}
                      onChange={(e) => setGameUrl(e.target.value)}
                      placeholder="https://boardgamegeek.com/boardgame/..."
                      className="input text-sm"
                    />
                  </div>
                </div>
              ) : (
                <div ref={dropdownRef} className="relative">
                  <input
                    type="text"
                    value={gameSearch}
                    onChange={(e) => {
                      setGameSearch(e.target.value);
                      setShowDropdown(true);
                      userHasTypedRef.current = true;
                    }}
                    onFocus={() => setShowDropdown(true)}
                    placeholder="Ieškoti žaidimo pagal pavadinimą..."
                    className="input"
                  />
                  {showDropdown && gameSearch.length >= 2 && (
                    <div className="absolute z-10 mt-1 max-h-72 w-full overflow-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                      {isSearching && !searchResults ? (
                        <div className="flex items-center justify-center p-4">
                          <LoadingSpinner className="h-5 w-5" />
                        </div>
                      ) : (
                        <>
                          {searchResults?.searchGames.map((game) => (
                            <button
                              key={game.id}
                              type="button"
                              onClick={() => {
                                setSelectedGame(game);
                                setCustomTitle("");
                                setGameUrl("");
                                setGameSearch("");
                                setShowDropdown(false);
                              }}
                              className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-gray-50"
                            >
                              {game.thumbnailUrl ? (
                                <img
                                  src={game.thumbnailUrl}
                                  alt={game.title}
                                  className="h-10 w-10 rounded object-cover"
                                />
                              ) : (
                                <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded">
                                  <GamePlaceholder />
                                </div>
                              )}
                              <div>
                                <p className="text-sm font-medium text-gray-900">{game.title}</p>
                                {game.yearPublished && (
                                  <p className="text-xs text-gray-500">{game.yearPublished}</p>
                                )}
                              </div>
                            </button>
                          ))}

                          {isBggSearching && (
                            <div className="flex items-center gap-2 border-t border-gray-100 px-4 py-3 text-xs text-gray-400">
                              <LoadingSpinner className="h-3 w-3" />
                              <span>Ieškoma BoardGameGeek...</span>
                            </div>
                          )}
                          {bggResults?.searchBggGame.results && bggResults.searchBggGame.results.length > 0 && (
                            <>
                              <div className="border-t border-gray-100 px-4 py-1.5 text-xs font-medium text-gray-400">
                                BoardGameGeek
                              </div>
                              {bggResults.searchBggGame.results.map((result) => (
                                <button
                                  key={`bgg-${result.bggId}`}
                                  type="button"
                                  onClick={() => handleImportBgg(result.bggId)}
                                  disabled={importingBggId === result.bggId}
                                  className="flex w-full items-center gap-3 px-4 py-2 text-left hover:bg-gray-50 disabled:opacity-50"
                                >
                                  <div className="flex h-10 w-10 items-center justify-center rounded bg-orange-50 text-xs font-medium text-orange-600">
                                    BGG
                                  </div>
                                  <div className="flex-1">
                                    <p className="text-sm font-medium text-gray-900">{result.title}</p>
                                    {result.yearPublished && (
                                      <p className="text-xs text-gray-500">{result.yearPublished}</p>
                                    )}
                                  </div>
                                  {importingBggId === result.bggId && (
                                    <LoadingSpinner className="h-4 w-4" />
                                  )}
                                </button>
                              ))}
                            </>
                          )}

                          {!isSearching && !isBggSearching && !searchResults?.searchGames.length && !bggResults?.searchBggGame.results?.length && (
                            <p className="p-4 text-sm text-gray-500">Žaidimų nerasta</p>
                          )}

                          {/* Continue without matching game */}
                          <div className="border-t border-gray-100 px-4 py-3">
                            <button
                              type="button"
                              onClick={() => {
                                setCustomTitle(gameSearch.trim());
                                setShowDropdown(false);
                              }}
                              disabled={!gameSearch.trim()}
                              className="text-sm text-primary-700 hover:text-primary-800 disabled:opacity-50"
                            >
                              Tęsti su pavadinimu „{gameSearch.trim()}"
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Condition, notes, price — single mode */}
          {!multiGameMode && (
            <>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Būklė
                </label>
                <select
                  value={condition}
                  onChange={(e) => setCondition(e.target.value)}
                  className="input"
                >
                  {CONDITIONS.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Būklės pastabos
                </label>
                <textarea
                  value={conditionNotes}
                  onChange={(e) => setConditionNotes(e.target.value)}
                  rows={3}
                  placeholder="Aprašykite žaidimo būklę detaliau..."
                  className="input"
                />
              </div>

              {!selectedGame?.languageIndependent && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Kalba
                  </label>
                  <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className="input"
                  >
                    {PRIORITY_LANGUAGES.map((code) => (
                      <option key={code} value={code}>
                        {LANGUAGE_LABELS[code]}
                      </option>
                    ))}
                    <option disabled>──────────</option>
                    {OTHER_LANGUAGES.map((code) => (
                      <option key={code} value={code}>
                        {LANGUAGE_LABELS[code]}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Kaina (&euro;)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  placeholder="0.00"
                  className="input"
                  required
                />
                {suggestedPriceInfo && (
                  <p className="mt-1 flex items-center gap-1.5 text-sm text-gray-500">
                    <span>Siūloma kaina:</span>
                    <button
                      type="button"
                      className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                      onClick={() => setPrice(String(getConditionAdjustedPrice(suggestedPriceInfo.newPrice, condition)))}
                    >
                      {(() => {
                        const adjusted = getConditionAdjustedPrice(suggestedPriceInfo.newPrice, condition);
                        return adjusted % 1 === 0 ? `${adjusted}` : adjusted.toFixed(2);
                      })()} &euro;
                    </button>
                    {selectedGame?.shopListings && selectedGame.shopListings.length > 0 && (
                      <PriceDetailsPopover
                        shopListings={selectedGame.shopListings}
                        suggestedPriceInfo={suggestedPriceInfo}
                        condition={condition}
                        onSelectPrice={(p) => setPrice(String(p))}
                      />
                    )}
                  </p>
                )}
              </div>
            </>
          )}

          {/* Per-game cards — multi-game mode */}
          {multiGameMode && multiGameEntries.some((e) => e.selected) && (
            <div className="space-y-4">
              {multiGameEntries.map((entry, i) => {
                if (!entry.selected) return null;
                return (
                  <div
                    key={i}
                    className="rounded-lg border border-gray-200 bg-gray-50 p-4"
                  >
                    <div className="mb-3 flex items-center gap-3">
                      {entry.gameInfo?.thumbnailUrl ? (
                        <img
                          src={entry.gameInfo.thumbnailUrl}
                          alt={entry.recognized.name}
                          className="h-10 w-10 rounded object-cover"
                        />
                      ) : (
                        <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded">
                          <GamePlaceholder />
                        </div>
                      )}
                      <p className="flex-1 text-sm font-semibold text-gray-900">
                        {entry.customTitle ||
                          entry.gameInfo?.title ||
                          entry.recognized.name}
                        {!entry.customTitle &&
                          (entry.gameInfo?.yearPublished || entry.recognized.year) && (
                            <span className="ml-1 font-normal text-gray-500">
                              ({entry.gameInfo?.yearPublished || entry.recognized.year})
                            </span>
                          )}
                      </p>
                      <button
                        type="button"
                        onClick={() => setEditingGameIndex(i)}
                        className="text-gray-400 hover:text-gray-600"
                        aria-label="Redaguoti žaidimą"
                        title="Redaguoti žaidimą"
                      >
                        <svg
                          className="h-4 w-4"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.536L16.732 3.732z"
                          />
                        </svg>
                      </button>
                    </div>

                    <div className="space-y-3">
                      <div className="flex gap-3">
                        <div className="flex-1">
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Būklė
                          </label>
                          <select
                            value={entry.condition}
                            onChange={(e) => {
                              updateMultiGameEntry(i, { condition: e.target.value });
                              if (i < multiGameEntries.length - 1) {
                                setConditionChangedIndexes((prev) => {
                                  const next = new Set(prev);
                                  next.add(i);
                                  return next;
                                });
                              }
                            }}
                            className="input text-sm"
                          >
                            {CONDITIONS.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option>
                            ))}
                          </select>
                          {conditionChangedIndexes.has(i) && i < multiGameEntries.length - 1 && (
                            <button
                              type="button"
                              onClick={() => applyConditionToBelow(i, entry.condition)}
                              className="mt-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                            >
                              Taikyti tą pačią žemiau esantiems žaidimams
                            </button>
                          )}
                        </div>
                        <div className="w-32">
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Kaina (&euro;)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={entry.price}
                            onChange={(e) =>
                              updateMultiGameEntry(i, { price: e.target.value })
                            }
                            placeholder="0.00"
                            className="input text-sm"
                            required
                          />
                          {entry.gameInfo?.suggestedPriceInfo != null && (
                            <p className="mt-1 flex items-center gap-1 text-xs text-gray-500">
                              <span>Siūloma:</span>
                              <button
                                type="button"
                                className="font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                onClick={() => updateMultiGameEntry(i, { price: String(getConditionAdjustedPrice(entry.gameInfo!.suggestedPriceInfo!.newPrice, entry.condition)) })}
                              >
                                {(() => {
                                  const adjusted = getConditionAdjustedPrice(entry.gameInfo!.suggestedPriceInfo!.newPrice, entry.condition);
                                  return adjusted % 1 === 0 ? `${adjusted}` : adjusted.toFixed(2);
                                })()} &euro;
                              </button>
                              {entry.gameInfo?.shopListings && entry.gameInfo.shopListings.length > 0 && (
                                <PriceDetailsPopover
                                  shopListings={entry.gameInfo.shopListings}
                                  suggestedPriceInfo={entry.gameInfo.suggestedPriceInfo}
                                  condition={entry.condition}
                                  onSelectPrice={(p) => updateMultiGameEntry(i, { price: String(p) })}
                                />
                              )}
                            </p>
                          )}
                        </div>
                      </div>

                      {!entry.gameInfo?.languageIndependent && (
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Kalba
                          </label>
                          <select
                            value={entry.language}
                            onChange={(e) =>
                              updateMultiGameEntry(i, { language: e.target.value })
                            }
                            className="input text-sm"
                          >
                            {PRIORITY_LANGUAGES.map((code) => (
                              <option key={code} value={code}>
                                {LANGUAGE_LABELS[code]}
                              </option>
                            ))}
                            <option disabled>──────────</option>
                            {OTHER_LANGUAGES.map((code) => (
                              <option key={code} value={code}>
                                {LANGUAGE_LABELS[code]}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <div>
                        <label className="mb-1 block text-xs font-medium text-gray-600">
                          Būklės pastabos
                        </label>
                        <textarea
                          value={entry.conditionNotes}
                          onChange={(e) =>
                            updateMultiGameEntry(i, { conditionNotes: e.target.value })
                          }
                          rows={2}
                          placeholder="Aprašykite būklę detaliau..."
                          className="input text-sm"
                        />
                      </div>

                      {!entry.gameInfo && (
                        <div>
                          <label className="mb-1 block text-xs font-medium text-gray-600">
                            Nuoroda į žaidimą (nebūtina)
                          </label>
                          <input
                            type="url"
                            value={entry.gameUrl}
                            onChange={(e) =>
                              updateMultiGameEntry(i, { gameUrl: e.target.value })
                            }
                            placeholder="https://boardgamegeek.com/boardgame/..."
                            className="input text-sm"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Delivery & Pickup options */}
          <div>
            <label className="mb-3 block text-sm font-semibold text-gray-900">
              Pristatymo būdai
            </label>
            <p className="mb-3 text-xs text-gray-500">
              Pasirinkite bent vieną pristatymo būdą
            </p>

            {/* Delivery toggle */}
            <div className="space-y-4">
              <div>
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={deliveryAvailable}
                      onChange={(e) => {
                        setDeliveryAvailable(e.target.checked);
                        setShippingAvailable(e.target.checked);
                      }}
                      className="peer sr-only"
                    />
                    <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white" />
                  </label>
                  <span className="text-sm font-medium text-gray-700">
                    Galimas pristatymas / siuntimas
                  </span>
                </div>

                {deliveryAvailable && (
                  <div className="ml-14 mt-3">
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Siuntimo kaina (&euro;)
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      value={shippingCost}
                      onChange={(e) => setShippingCost(e.target.value)}
                      placeholder="0.00"
                      className="input w-40"
                    />
                    <DeliveryPriceSuggestions
                      suggestions={deliverySuggestions}
                      onSelect={(price) => setShippingCost(String(price))}
                    />
                  </div>
                )}
              </div>

              {/* Pickup toggle */}
              <div>
                <div className="flex items-center gap-3">
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      checked={pickupAvailable}
                      onChange={(e) => setPickupAvailable(e.target.checked)}
                      className="peer sr-only"
                    />
                    <div className="h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary-800 peer-checked:after:translate-x-full peer-checked:after:border-white" />
                  </label>
                  <span className="text-sm font-medium text-gray-700">
                    Galimas asmeninis atsiėmimas
                  </span>
                </div>

                {pickupAvailable && (
                  <div className="ml-14 mt-3 space-y-3">
                    {/* Pickup radius - always visible as select */}
                    <div>
                      <label className="mb-1 block text-sm text-gray-600">
                        Atsiėmimo spindulys
                      </label>
                      <select
                        value={pickupRadius}
                        onChange={(e) => setPickupRadius(e.target.value)}
                        className="input w-40"
                      >
                        <option value="0">Tik mieste</option>
                        <option value="1">1 km</option>
                        <option value="2">2 km</option>
                        <option value="3">3 km</option>
                        <option value="5">5 km</option>
                        <option value="10">10 km</option>
                        <option value="15">15 km</option>
                        <option value="-1">Visoje Lietuvoje</option>
                      </select>
                    </div>

                    {/* Compact address display when user has saved location */}
                    {userHasLocation && !showAddressEditor ? (
                      <div>
                        <label className="mb-1 block text-sm text-gray-600">
                          Atsiėmimo vieta
                        </label>
                        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                          <svg
                            className="h-4 w-4 shrink-0 text-green-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                          </svg>
                          <span className="text-sm text-gray-700">
                            {pickupCity || user?.city || "Vieta nustatyta"}
                          </span>
                          <button
                            type="button"
                            onClick={() => setShowAddressEditor(true)}
                            className="ml-auto text-sm font-medium text-primary-800 hover:text-primary-900"
                          >
                            Keisti
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <LocationAutocomplete
                          label="Atsiėmimo vieta"
                          value={pickupCity}
                          latitude={pickupLatitude}
                          longitude={pickupLongitude}
                          radius={Number(pickupRadius)}
                          onChange={(data) => {
                            setPickupCity(data.city || data.address);
                            setPickupLatitude(data.latitude);
                            setPickupLongitude(data.longitude);
                            // Also update legacy location field
                            setLocation(data.city || data.address);
                            // If user changed location from default, uncheck save-as-default
                            if (userHasLocation) {
                              const isSameAsDefault =
                                data.latitude != null &&
                                user?.latitude != null &&
                                Math.abs(data.latitude - user.latitude) < 0.001 &&
                                data.longitude != null &&
                                user?.longitude != null &&
                                Math.abs(data.longitude - user.longitude) < 0.001;
                              setSaveAsDefaultLocation(isSameAsDefault && saveAsDefaultLocation);
                            }
                          }}
                          placeholder="pvz. Vilnius"
                        />

                        {/* Save as default location checkbox */}
                        {pickupLatitude != null && pickupLongitude != null && (
                          <label className="flex items-center gap-2 text-sm text-gray-600">
                            <input
                              type="checkbox"
                              checked={saveAsDefaultLocation}
                              onChange={(e) => setSaveAsDefaultLocation(e.target.checked)}
                              className="h-4 w-4 rounded border-gray-300 text-primary-800 focus:ring-primary-800"
                            />
                            Išsaugoti kaip numatytąją vietą
                          </label>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            </div>

            {!deliveryAvailable && !pickupAvailable && (
              <p className="mt-2 text-sm text-red-600">
                Pasirinkite bent vieną pristatymo būdą
              </p>
            )}
          </div>
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="btn-primary w-full"
        >
          {isSubmitting
            ? "Kuriama..."
            : multiGameMode
              ? `Sukurti ${selectedCount} skelbim${selectedCount === 1 ? "ą" : selectedCount < 10 ? "us" : "ų"}`
              : "Sukurti skelbimą"}
        </button>
      </form>
      )}

      <Modal
        open={editingGameIndex !== null}
        onClose={() => setEditingGameIndex(null)}
        title="Redaguoti žaidimą"
        size="2xl"
      >
        {editingGameIndex !== null && multiGameEntries[editingGameIndex] && (() => {
          const entry = multiGameEntries[editingGameIndex];
          const idx = editingGameIndex;
          const setEntryBbox = (box: BoundingBox | null) =>
            updateMultiGameEntry(idx, {
              recognized: { ...entry.recognized, boundingBox: box },
            });
          const otherEntriesAsSiblings: ListingSibling[] = multiGameEntries
            .map((e, i) => ({ e, i }))
            .filter(({ e, i }) => i !== idx && e.selected && e.recognized.boundingBox)
            .map(({ e, i }) => ({
              id: `entry-${i}`,
              status: "active",
              boundingBox: e.recognized.boundingBox!,
              gameTitle: e.customTitle || e.gameInfo?.title || e.recognized.name,
              price: null,
            }));
          return (
            <div className="space-y-4">
              {images[0] && (
                <div>
                  <label className="mb-1 block text-xs font-medium text-gray-600">
                    Nuotraukos sritis
                  </label>
                  <BoundingBoxOverlay
                    mode="edit"
                    activeBoundingBox={entry.recognized.boundingBox ?? null}
                    siblings={otherEntriesAsSiblings}
                    imageUrl={images[0].url}
                    readOnlySiblings
                    hideDeleteButton
                    onAddBox={(box) => setEntryBbox(box)}
                    onRemoveSibling={() => {}}
                    onRemoveCurrentBox={() => setEntryBbox(null)}
                    onUpdateActiveBox={(box) => setEntryBbox(box)}
                    onUpdateSiblingBox={() => {}}
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Tempkite kampus, kad pakoreguotumėte šio žaidimo sritį. Nubrėžkite naują — pakeisite esamą.
                  </p>
                </div>
              )}

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Pavadinimas
                </label>
                <input
                  type="text"
                  value={entry.customTitle}
                  onChange={(e) =>
                    updateMultiGameEntry(idx, { customTitle: e.target.value })
                  }
                  placeholder={entry.gameInfo?.title || entry.recognized.name}
                  className="input text-sm"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Palikite tuščią, kad būtų naudojamas susieto žaidimo pavadinimas
                </p>
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  Susietas BGG žaidimas
                </label>
                {entry.gameInfo ? (
                  <div className="flex items-center gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                    {entry.gameInfo.thumbnailUrl ? (
                      <img
                        src={entry.gameInfo.thumbnailUrl}
                        alt={entry.gameInfo.title}
                        className="h-10 w-10 rounded object-cover"
                      />
                    ) : (
                      <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded">
                        <GamePlaceholder />
                      </div>
                    )}
                    <div className="flex-1">
                      <p className="text-sm font-medium text-gray-900">
                        {entry.gameInfo.title}
                      </p>
                      {entry.gameInfo.yearPublished && (
                        <p className="text-xs text-gray-500">
                          {entry.gameInfo.yearPublished}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() =>
                        updateMultiGameEntry(idx, { gameInfo: null })
                      }
                      className="text-sm text-gray-500 hover:text-gray-700"
                    >
                      Atsieti
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">Nėra susieto žaidimo</p>
                )}
              </div>

              <div>
                <label className="mb-1 block text-xs font-medium text-gray-600">
                  {entry.gameInfo ? "Susieti kitą žaidimą" : "Susieti žaidimą"}
                </label>
                <GameSelector
                  initialSearch={entry.customTitle || entry.recognized.name}
                  size="sm"
                  autoFocus={false}
                  onSelectGame={(game) => {
                    updateMultiGameEntry(idx, {
                      gameInfo: game,
                      customTitle: "",
                      gameUrl: "",
                      language: game.languageIndependent
                        ? "universal"
                        : entry.language,
                      price: game.suggestedPriceInfo
                        ? String(
                            getConditionAdjustedPrice(
                              game.suggestedPriceInfo.newPrice,
                              entry.condition
                            )
                          )
                        : entry.price,
                    });
                    setEditingGameIndex(null);
                  }}
                  onCustomTitle={(title) => {
                    updateMultiGameEntry(idx, {
                      gameInfo: null,
                      customTitle: title,
                    });
                    setEditingGameIndex(null);
                  }}
                />
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setEditingGameIndex(null)}
                  className="btn-primary"
                >
                  Gerai
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>
    </div>
  );
}
